import React from "react";
import { connect } from "react-redux";

import List from "../core/components/list/List";
import ListItem from "../core/components/list/ListItem";
import {
    Alignment,
    FlexBehaviour,
    FlexDirection,
    Side,
    Size,
} from "../core/types/system/Layout";
import GeneralContainer from "../core/components/containers/GeneralContainer";
import MainToolbarContents from "../core/components/toolbar/MainToolbarContents";
import ButtonMenu from "../core/components/buttons/ButtonMenu";
import Button from "../core/components/buttons/Button";
import { addAlert } from "../core/state/system/Alerts";
import { AlertTypes } from "../core/types/system/AlertEntry";
import { unwrappedDispatch } from "../core/foundation/Dispatching";
import { setPageLoading } from "../core/state/system/UIConfiguration";
import Collapsible from "../core/components/collapsible/Collapsible";
import ContextFragment from "../core/components/contextmenu/ContextFragment";
import ContextMenuItem from "../core/components/contextmenu/ContextMenuItem";
import QueuedExportFile from "../core/types/system/QueuedExportFile";
import { addExports } from "../core/state/administration/Exports";
import User from "../core/types/core/User";
import { openRecycled, RecycledType } from "../core/state/system/Recycled";
import {
    deleteLISSIntegration,
    deleteSIFIntegration,
    fetchLISSAdditionalConfig,
    initialiseLISSIntegrations,
    initialiseSIFIntegrations,
    resetLISSCallState,
    setLISSCallState,
    syncLISSIntegration,
    syncSIFIntegration,
    updateLISSIntegration,
    updateSIFIntegration,
    validateLISSIntegration,
    setIntegrationLogPageAndSize,
    initialiseLog,
    reinitialiseLog,
    updateLISSIntegrationResolutions,
} from "../core/state/core/Integrations";
import LISSIntegration from "../core/types/core/LISSIntegration";
import SIFIntegration, {
    SIFAuthModes,
} from "../core/types/core/SIFIntegration";
import Campus from "../core/types/core/Campus";
import { initialiseCampusDetails } from "../core/state/core/Campus";
import Text from "../core/components/text/Text";
import { IAppState } from "../core/state/Store";

import "./Integrations.scss";
import ToolbarButton from "../core/components/toolbar/ToolbarButton";
import Dialog from "../core/components/dialog/Dialog";
import DialogFooter from "../core/components/dialog/DialogFooter";
import DialogOK from "../core/components/dialog/DialogOK";
import DialogCancel from "../core/components/dialog/DialogCancel";
import DialogContent from "../core/components/dialog/DialogContent";
import InputFormMandatory from "../core/components/inputform/InputFormMandatory";
import Panel from "../core/components/panels/Panel";
import InputForm from "../core/components/inputform/InputForm";
import RenderOrientations from "../core/types/enums/RenderOrientation";
import InputFormField from "../core/components/inputform/InputFormField";
import InputFieldTypes from "../core/types/enums/ImportFieldTypes";
import Utility from "../core/foundation/Utility";
import Info from "../core/components/info/Info";
import LISSCallType from "../core/types/core/LISSCallType";
import Grid from "../core/components/grid/Grid";
import GridColumn, { GridColumnType } from "../core/components/grid/GridColumn";
import { AppContext } from "../core/types/system/AppContext";
import SelectList from "../core/components/inputform/SelectList";
import Spacer from "../core/components/containers/Spacer";
import { LISSResolutionRule } from "../core/types/core/LISSResolutionRule";
import IntegrationErrorLog from "../core/types/core/IntegrationErrorLog";
import PageSpecification from "../core/types/system/PageSpecification";
import LISSTimetableStructure from "../core/types/core/LISSTimetableStructure";
import RenderOrientation from "../core/types/enums/RenderOrientation";

interface IntegrationsProps {
    user: User;
    campuses: Campus[];
    lissData: LISSIntegration[];
    sifData: SIFIntegration[];
    lissResolutions: LISSResolutionRule[];
    logPageData: IntegrationErrorLog[];
    pageSpec: PageSpecification;

    setPageLoading: (loading: boolean) => void;
    initialiseCampusDetails: () => Promise<void>;
    initialiseLISSIntegrations: () => Promise<void>;
    initialiseSIFIntegrations: () => Promise<void>;
    syncLISSIntegration: (integration: LISSIntegration) => Promise<void>;
    validateLISSIntegration: (integration: LISSIntegration) => Promise<void>;
    syncSIFIntegration: (integration: SIFIntegration) => Promise<void>;
    resetLISSCallState: () => void;
    updateSIFIntegration: (integration: SIFIntegration) => Promise<void>;
    updateLISSIntegration: (integration: LISSIntegration) => Promise<void>;
    updateLISSIntegrationResolutions: (
        integration: LISSIntegration,
        resolutions: LISSResolutionRule[],
    ) => Promise<void>;
    deleteLISSIntegration: (integration: LISSIntegration) => Promise<void>;
    deleteSIFIntegration: (integration: SIFIntegration) => Promise<void>;
    addExports: (exports: QueuedExportFile[]) => Promise<void>;
    openRecycled: (types: RecycledType[]) => void;
    initialiseLog: (type: string, id: number) => Promise<void>;
    reinitialiseLog: (type: string, id: number) => Promise<void>;
    setIntegrationLogPageAndSize: (
        specification: PageSpecification,
    ) => Promise<void>;
}

interface IntegrationsState {
    groupsOpen: Map<string, boolean>;
    all: (LISSIntegration | SIFIntegration)[];
    integrations: {
        typeName: string;
        open: boolean;
        integrations: (LISSIntegration | SIFIntegration)[];
    }[];
    selections: (LISSIntegration | SIFIntegration)[];

    editOpen: boolean;
    editLoad: boolean;
    editTitle: string;
    editing?: LISSIntegration | SIFIntegration;
    editingCampus?: Campus;

    logsOpen?: boolean;
    logsTitle?: string;
    logsData?: IntegrationErrorLog[];

    validationOpen?: boolean;
    validationLoading?: boolean;
    validationData?: LISSResolutionRule[];

    addType?: "LISS" | "SIF";
}

class Integrations extends React.Component<
    IntegrationsProps,
    IntegrationsState
> {
    static contextType = AppContext;
    context!: React.ContextType<typeof AppContext>;

    state: IntegrationsState = {
        groupsOpen: new Map(),
        all: [],
        integrations: [],
        selections: [],

        editOpen: false,
        editLoad: false,
        editTitle: "",
        editing: undefined,

        validationOpen: false,
        validationLoading: false,
    };

    static getDerivedStateFromProps(
        nextProps: IntegrationsProps,
        prevState: IntegrationsState,
    ): IntegrationsState | null {
        return {
            ...prevState,
            editingCampus: nextProps.campuses.find(
                c => c.id === prevState.editing?.campusID,
            ),
        };
    }

    async componentDidMount() {
        try {
            this.props.setPageLoading(true);
            await Promise.all([
                this.props.initialiseCampusDetails(),
                this.props.initialiseLISSIntegrations(),
                this.props.initialiseSIFIntegrations(),
            ]);
            this.buildState();
        } finally {
            this.props.setPageLoading(false);
        }
    }

    componentDidUpdate(
        prevProps: IntegrationsProps,
        prevState: IntegrationsState,
    ) {
        if (
            prevProps.lissData !== this.props.lissData ||
            prevProps.sifData !== this.props.sifData ||
            prevState.groupsOpen !== this.state.groupsOpen
        )
            this.buildState();
    }

    buildState() {
        this.setState(prevState => ({
            groupsOpen: prevState.groupsOpen,
            all: [...this.props.lissData, ...this.props.sifData],
            integrations: [
                {
                    typeName: "LISS Provider",
                    open: prevState.groupsOpen.get("LISS Provider") ?? true,
                    integrations: this.props.lissData,
                },
                {
                    typeName: "SIF Provider",
                    open: prevState.groupsOpen.get("SIF Provider") ?? true,
                    integrations: this.props.sifData,
                },
            ],
            selections: prevState.selections,
        }));
    }

    toggleOpen(groupName: string, open: boolean) {
        this.setState(prevState => {
            const newGroupsOpen = new Map(prevState.groupsOpen);
            newGroupsOpen.set(groupName, open);
            return {
                groupsOpen: newGroupsOpen,
            };
        });
    }

    selectAll() {
        this.setState({
            selections: [...this.props.lissData, ...this.props.sifData],
        });
    }
    deselectAll() {
        this.setState({
            selections: [],
        });
    }

    expandAll() {
        this.setState(prevState => {
            const newGroupsOpen = new Map(prevState.groupsOpen);
            for (let group of this.state.integrations) {
                newGroupsOpen.set(group.typeName, true);
            }
            return {
                groupsOpen: newGroupsOpen,
            };
        });
    }

    collapseAll() {
        this.setState(prevState => {
            const newGroupsOpen = new Map(prevState.groupsOpen);
            for (let group of this.state.integrations) {
                newGroupsOpen.set(group.typeName, false);
            }
            return {
                groupsOpen: newGroupsOpen,
            };
        });
    }

    add() {
        this.setState({
            editOpen: true,
            editLoad: false,
            editTitle: `Add Integration`,
            editing: undefined,
        });
    }

    addType(type: "LISS" | "SIF") {
        const integration =
            type === "LISS" ? new LISSIntegration() : new SIFIntegration();
        this.setState({
            addType: type,
            editTitle: `Edit ${integration.type} Integration`,
            editing: integration,
        });
    }

    edit(file?: LISSIntegration | SIFIntegration) {
        const integration = file || this.state.selections[0];
        if (!integration) return;

        const editing = integration.clone();

        this.setState({
            editOpen: true,
            editLoad: false,
            editTitle: `Edit ${integration.type} Integration`,
            editing: editing,
        });
    }

    async delete(file?: LISSIntegration | SIFIntegration) {
        const integrations = file ? [file] : this.state.selections;

        const result = await addAlert({
            type: AlertTypes.Confirm,
            text:
                "Are you sure you want to delete these integrations?<br />" +
                `<ul class="indented chevron"><li>${integrations
                    .map(item => item.descriptor)
                    .join("</li><li>")}</li></ul>`,
            okText: "Yes",
            cancelText: "No",
        });

        if (result.response) {
            try {
                this.props.setPageLoading(true);
                await Promise.all(
                    integrations.map(integration => {
                        if (integration.type === "LISS") {
                            return this.props.deleteLISSIntegration(
                                integration as LISSIntegration,
                            );
                        } else {
                            return this.props.deleteSIFIntegration(
                                integration as SIFIntegration,
                            );
                        }
                    }),
                );
                this.setState({
                    selections: [],
                });
            } finally {
                this.props.setPageLoading(false);
            }
        }
    }

    async sync(file?: LISSIntegration | SIFIntegration) {
        const integrations = file ? [file] : this.state.selections;

        const result = await addAlert({
            type: AlertTypes.Confirm,
            text:
                "Are you sure you want to synchronise these integrations?<br />" +
                `<ul class="indented chevron"><li>${integrations
                    .map(item => item.descriptor)
                    .join("</li><li>")}</li></ul>`,
            okText: "Yes",
            cancelText: "No",
        });

        if (result.response) {
            try {
                this.props.setPageLoading(true);
                await Promise.all(
                    integrations.map(integration => {
                        if (integration.type === "LISS") {
                            return this.props.syncLISSIntegration(
                                integration as LISSIntegration,
                            );
                        } else {
                            return this.props.syncSIFIntegration(
                                integration as SIFIntegration,
                            );
                        }
                    }),
                );
            } finally {
                this.props.setPageLoading(false);
            }
        }
    }

    async validate() {
        try {
            this.props.setPageLoading(true);

            await this.props.validateLISSIntegration(
                this.state.editing as LISSIntegration,
            );

            addAlert({
                icon: "checklist",
                title: "Validation Requested",
                text:
                    "Validation of data for this integration has " +
                    "been requested.\n\nResults will be emailed to " +
                    "the administration user within 15 minutes",
            });
        } finally {
            this.props.setPageLoading(false);
        }
    }

    fixValidation() {
        this.setState({
            validationOpen: true,
            validationData: this.props.lissResolutions.map(
                rule => new LISSResolutionRule(rule),
            ),
        });
    }

    closeValidation() {
        this.setState({
            validationOpen: false,
            validationLoading: false,
            validationData: undefined,
        });
    }

    async saveValidation() {
        if (!this.state.editing) return;

        try {
            this.setState({
                validationLoading: true,
            });

            await this.props.updateLISSIntegrationResolutions(
                this.state.editing as LISSIntegration,
                this.state.validationData ?? [],
            );
        } finally {
            this.setState({
                validationLoading: false,
            });

            this.closeValidation();
        }
    }

    async viewLog() {
        if (!this.state.editing) return;

        this.props.initialiseLog(
            this.state.editing.type,
            this.state.editing.id,
        );

        this.setState({
            logsOpen: true,
            logsTitle: `${this.state.editing.type} Log - ${this.state.editing.descriptor}`,
        });
    }

    getCampusName(campusId: number): string {
        return (
            this.props.campuses.find(c => c.id === campusId)?.name ??
            "Unknown Campus"
        );
    }

    canSave() {
        if (!this.state.editing) return false;

        switch (this.state.editing.type) {
            case "LISS":
                const liss = this.state.editing as LISSIntegration;
                return (
                    liss.providerUrl.trim() !== "" &&
                    liss.campusID > 0 &&
                    liss.termDatesID > 0
                );
            case "SIF":
                const sif = this.state.editing as SIFIntegration;
                return (
                    sif.providerURL.trim() !== "" &&
                    sif.name.trim() !== "" &&
                    sif.applicationKey.trim() !== "" &&
                    sif.sharedSecret.trim() !== "" &&
                    sif.campusID > 0 &&
                    sif.termDatesID > 0
                );
        }

        return false;
    }

    async saveEdit() {
        if (await this.context.verifyCredentials()) {
            try {
                this.setState({
                    editLoad: true,
                });

                switch (this.state.editing?.type) {
                    case "LISS":
                        await this.props.updateLISSIntegration(
                            this.state.editing as LISSIntegration,
                        );
                        break;
                    case "SIF":
                        await this.props.updateSIFIntegration(
                            this.state.editing as SIFIntegration,
                        );
                        break;
                }
            } finally {
                this.setState({
                    editLoad: false,
                });
            }
        }
    }

    cancelEdit() {
        this.setState({
            editOpen: false,
            editLoad: false,
            editTitle: "",
            editing: undefined,
            editingCampus: undefined,
        });
        this.props.resetLISSCallState();
    }

    hasResolutions() {
        return this.props.lissResolutions?.length > 0;
    }

    hasUnresolved() {
        return this.props.lissResolutions?.some(rule => !rule.actionID);
    }

    reloadLog() {
        this.props.reinitialiseLog(
            this.state.editing.type,
            this.state.editing.id,
        );
    }

    downloadLog() {
        let output = "";

        output += "Date,Error Message";

        this.props.logPageData.forEach(entry => {
            if (!!entry?.date && !!entry?.message)
                output += `\n"${entry.date?.toLocaleShort() ?? ""}","${
                    entry.message?.replace(/"/g, '""') ?? ""
                }"`;
        });

        output
            .toBase64()
            .download(
                `Integration_${this.state.editing?.descriptor
                    .replace(/ /g, "_")
                    .replace(/-/g, "_")}_Log_Page${
                    this.props.pageSpec.page + 1
                }.csv`,
                "text/csv",
            );
    }

    closeLog() {
        this.setState({
            logsOpen: false,
        });
    }

    render() {
        return (
            <>
                <section data-contextkey="integrations-menu">
                    <MainToolbarContents>
                        <ButtonMenu
                            icon="description"
                            text="Files"
                            tooltip="File management options">
                            <Button
                                icon="checklist_rtl"
                                text="Select All"
                                onClick={() => this.selectAll()}
                            />
                            <Button
                                icon="event_list"
                                text="Deselect All"
                                onClick={() => this.deselectAll()}
                            />
                            <Button
                                icon="add"
                                text="Expand All"
                                onClick={() => this.expandAll()}
                            />
                            <Button
                                icon="remove"
                                text="Collapse All"
                                onClick={() => this.collapseAll()}
                            />
                            <Button
                                icon="delete"
                                text="Delete Selected Items"
                                disabled={!this.state.selections.length}
                                onClick={() => this.delete()}
                            />
                            <Button
                                icon="sync"
                                text="Synchronise Selected Items"
                                disabled={!this.state.selections.length}
                                onClick={() => this.sync()}
                            />
                        </ButtonMenu>
                        <ToolbarButton
                            icon="add"
                            text="Add"
                            onClick={() => this.add()}
                        />
                        <ToolbarButton
                            icon="edit"
                            text="Edit"
                            disabled={this.state.selections.length !== 1}
                            onClick={() => this.edit()}
                        />
                    </MainToolbarContents>

                    {this.state?.all?.length > 0 ? (
                        <GeneralContainer
                            scaling={true}
                            mid={true}
                            max={true}
                            bufferSide={Side.All}
                            clearanceSide={Side.Top}
                            clearanceSize={Size.Large}
                            alignment={Alignment.Center}>
                            {this.state.integrations
                                .filter(group => group.integrations.length)
                                .map((group, index) => (
                                    <Collapsible
                                        key={index}
                                        open={group.open}
                                        name={group.typeName}
                                        clearanceSide={Side.Bottom}
                                        onOpenChange={open =>
                                            this.toggleOpen(
                                                group.typeName,
                                                open,
                                            )
                                        }>
                                        <List<LISSIntegration | SIFIntegration>
                                            joined={true}
                                            multiSelect={true}
                                            selections={this.state.selections}
                                            selectionChanged={(_, selections) =>
                                                this.setState({
                                                    selections,
                                                })
                                            }>
                                            {group.integrations.map(
                                                (file, index) => (
                                                    <ListItem<
                                                        | LISSIntegration
                                                        | SIFIntegration
                                                    >
                                                        key={index}
                                                        data={file}
                                                        panel={true}
                                                        column={true}
                                                        autoHeight={true}
                                                        selectable={true}
                                                        bufferSide={Side.All}
                                                        contextkey="integrations-file"
                                                        contextvalue={`${file.type}-${file.id}`}
                                                        onDoubleClick={() =>
                                                            this.edit(file)
                                                        }>
                                                        <div className="flex">
                                                            <div className="twothirds">
                                                                <h3 className="truncated">
                                                                    {file.descriptor +
                                                                        " - " +
                                                                        this.getCampusName(
                                                                            file.campusID,
                                                                        )}
                                                                </h3>
                                                            </div>
                                                            <div
                                                                className={`onethird text-right ${
                                                                    file.syncFailed
                                                                        ? "error-message"
                                                                        : ""
                                                                }`}>
                                                                <b>
                                                                    Last
                                                                    Sync:&nbsp;
                                                                </b>

                                                                {file.lastSync ? (
                                                                    <span>
                                                                        {file.lastSync.toLocale()}
                                                                    </span>
                                                                ) : (
                                                                    <span>
                                                                        Not yet
                                                                        synchronised
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div className="top-buffer flex-row flex-wrapped spaced">
                                                            <div>
                                                                <b>
                                                                    Provider:&nbsp;
                                                                </b>
                                                                <span
                                                                    className="truncated"
                                                                    style={{
                                                                        maxWidth:
                                                                            "500px",
                                                                    }}>
                                                                    {
                                                                        file.providerURL
                                                                    }
                                                                </span>
                                                            </div>

                                                            <div>
                                                                <b>
                                                                    User:&nbsp;
                                                                </b>
                                                                <span>
                                                                    {file.user ??
                                                                        "User per session"}
                                                                </span>
                                                            </div>

                                                            {file.type ===
                                                                "LISS" && (
                                                                <div>
                                                                    <b>
                                                                        Schema:&nbsp;
                                                                    </b>
                                                                    <span>
                                                                        {file.schema ===
                                                                        "Compass"
                                                                            ? "Compass Education"
                                                                            : "Standard"}
                                                                    </span>
                                                                </div>
                                                            )}

                                                            <div>
                                                                <b>
                                                                    Next
                                                                    Sync:&nbsp;
                                                                </b>
                                                                {file.active ? (
                                                                    <span>
                                                                        {
                                                                            file.syncAtTime
                                                                        }
                                                                    </span>
                                                                ) : (
                                                                    <span>
                                                                        Manual
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </ListItem>
                                                ),
                                            )}
                                        </List>
                                    </Collapsible>
                                ))}
                        </GeneralContainer>
                    ) : (
                        <Text clearanceSide={Side.All}>
                            No integrations found.
                        </Text>
                    )}
                </section>

                <Dialog
                    showDialog={this.state.editOpen}
                    showLoading={this.state.editLoad}
                    dialogIcon="edit"
                    dialogTitle={this.state.editTitle}
                    canResize={false}
                    width={750}>
                    <DialogContent>
                        {this.state.editing?.id === 0 && (
                            <GeneralContainer clearanceSide={Side.Bottom}>
                                <SelectList
                                    tabs={true}
                                    value={this.state.addType}
                                    options={[
                                        {
                                            text: "LISS Provider",
                                            value: "LISS",
                                        },
                                        {
                                            text: "SIF Provider",
                                            value: "SIF",
                                        },
                                    ]}
                                    onChange={value => this.addType(value)}
                                />
                            </GeneralContainer>
                        )}
                        {this.state.editing && (
                            <>
                                <Panel>
                                    <Panel>
                                        {this.state.editing?.type === "SIF" && (
                                            <SIFIntegrationForm
                                                binding={
                                                    this.state
                                                        .editing as SIFIntegration
                                                }
                                                campus={
                                                    this.state.editingCampus
                                                }
                                                campuses={this.props.campuses}
                                                onChange={value =>
                                                    this.setState({
                                                        editing:
                                                            new SIFIntegration(
                                                                value,
                                                            ),
                                                    })
                                                }
                                            />
                                        )}
                                        {this.state.editing?.type ===
                                            "LISS" && (
                                            <ConnectedLISSIntegrationForm
                                                binding={
                                                    this.state
                                                        .editing as LISSIntegration
                                                }
                                                campus={
                                                    this.state.editingCampus
                                                }
                                                campuses={this.props.campuses}
                                                onChange={value =>
                                                    this.setState({
                                                        editing:
                                                            new LISSIntegration(
                                                                value,
                                                            ),
                                                    })
                                                }
                                                onLoadingChanged={loading =>
                                                    this.setState({
                                                        editLoad: loading,
                                                    })
                                                }
                                            />
                                        )}
                                    </Panel>
                                </Panel>

                                <InputFormMandatory />
                            </>
                        )}
                        {this.state.editing?.id > 0 && (
                            <Text
                                className={
                                    this.hasUnresolved() ? "error-message" : ""
                                }
                                clearanceSide={Side.Top}>
                                {this.state.editing?.lastSync ? (
                                    <>
                                        Last synced on{" "}
                                        {this.state.editing?.lastSync.toLocaleShort()}
                                    </>
                                ) : (
                                    <>Not yet synced</>
                                )}
                                {this.hasUnresolved() && (
                                    <>
                                        {" "}
                                        (Last sync attempt failed validation,
                                        click below to fix)
                                    </>
                                )}
                            </Text>
                        )}
                    </DialogContent>
                    <DialogFooter full={true}>
                        {this.state.editing?.id > 0 && (
                            <GeneralContainer
                                flexSpaced={true}
                                flex={FlexDirection.Row}
                                flexBehaviour={FlexBehaviour.Max}>
                                {this.state.editing?.type === "LISS" && (
                                    <DialogOK
                                        text="Validate"
                                        onClick={this.validate.bind(this)}
                                    />
                                )}
                                {this.state.editing?.type === "LISS" &&
                                    this.hasResolutions() && (
                                        <DialogOK
                                            text={
                                                this.hasUnresolved()
                                                    ? "Fix Validation Issues"
                                                    : "Edit Validation Rules"
                                            }
                                            onClick={this.fixValidation.bind(
                                                this,
                                            )}
                                        />
                                    )}
                                <DialogCancel
                                    text="Synchronise"
                                    onClick={this.sync.bind(
                                        this,
                                        this.state.editing,
                                    )}
                                />
                                <DialogCancel
                                    text="View Log"
                                    onClick={this.viewLog.bind(this)}
                                />
                            </GeneralContainer>
                        )}

                        <Spacer />

                        <DialogCancel
                            text="Cancel"
                            onClick={this.cancelEdit.bind(this)}
                        />
                        <DialogOK
                            text="Save"
                            secure={true}
                            disabled={!this.canSave()}
                            onClick={this.saveEdit.bind(this)}
                        />
                    </DialogFooter>
                </Dialog>

                <Dialog
                    showDialog={this.state.validationOpen}
                    showLoading={this.state.validationLoading}
                    dialogIcon="article"
                    dialogTitle="Fix Validation Issues"
                    zIndex={1000}
                    width={750}>
                    <DialogContent>
                        {this.state.validationData?.length > 0 ? (
                            this.state.validationData.map((rule, index) => (
                                <Panel
                                    key={index}
                                    clearanceSide={Side.Bottom}
                                    title="Rule violation:">
                                    <Text>
                                        <i
                                            className={`inline ${
                                                rule.isValid ? "green" : "error"
                                            }`}>
                                            {rule.isValid
                                                ? "check_circle"
                                                : "error_outline"}
                                        </i>
                                        &nbsp;
                                        <span className="inline">
                                            {rule.name}
                                        </span>
                                    </Text>
                                    <InputForm
                                        type={LISSResolutionRule}
                                        binding={rule}
                                        display={RenderOrientation.Vertical}
                                        onChange={value => {
                                            value.action =
                                                value.availableResolutions.find(
                                                    action =>
                                                        action.id ===
                                                        value.actionID,
                                                );
                                            const validationData = [
                                                ...this.state.validationData,
                                            ];
                                            const index =
                                                validationData.findIndex(
                                                    r => r.id === rule.id,
                                                );
                                            if (index !== -1) {
                                                validationData[index] =
                                                    new LISSResolutionRule({
                                                        ...validationData[
                                                            index
                                                        ],
                                                        ...value,
                                                    });
                                                this.setState({
                                                    validationData,
                                                });
                                            }
                                        }}>
                                        <InputFormField
                                            type={InputFieldTypes.Select}
                                            field="actionID"
                                            label="Resolution Action"
                                            labelBold={true}
                                            options={rule.availableResolutions.map(
                                                action => ({
                                                    text: action.name,
                                                    value: action.id,
                                                }),
                                            )}
                                        />
                                        {rule.action?.fieldNames?.length >
                                            0 && (
                                            <InputFormField
                                                key={index}
                                                type={InputFieldTypes.Text}
                                                field="actionData1"
                                                label={
                                                    rule.action.fieldNames[0]
                                                }
                                            />
                                        )}
                                        {rule.action?.fieldNames?.length >
                                            1 && (
                                            <InputFormField
                                                key={index}
                                                type={InputFieldTypes.Text}
                                                field="actionData2"
                                                label={
                                                    rule.action.fieldNames[1]
                                                }
                                            />
                                        )}
                                        <InputFormField
                                            type={InputFieldTypes.Check}
                                            reverse={true}
                                            field="executeForAll"
                                            label="Run action for all items? (regardless of whether valid or not)"
                                        />
                                    </InputForm>
                                </Panel>
                            ))
                        ) : (
                            <div>No validation issues available</div>
                        )}
                    </DialogContent>
                    <DialogFooter>
                        <DialogCancel
                            text="Close"
                            onClick={this.closeValidation.bind(this)}
                        />
                        <DialogOK
                            text="Save and Close"
                            disabled={
                                !this.state.validationData?.every(
                                    rule => rule.isValid,
                                )
                            }
                            onClick={this.saveValidation.bind(this)}
                        />
                    </DialogFooter>
                </Dialog>

                <Dialog
                    showDialog={this.state.logsOpen}
                    showLoading={this.props.pageSpec.loading}
                    dialogIcon="article"
                    dialogTitle={this.state.logsTitle}
                    canResize={true}
                    zIndex={1000}
                    width={750}>
                    <DialogContent>
                        {this.props.logPageData?.length > 0 ? (
                            <Grid<IntegrationErrorLog>
                                value={this.props.logPageData}
                                fullWidth={true}
                                keyField="id"
                                pageSpecification={this.props.pageSpec}
                                onPageChange={
                                    this.props.setIntegrationLogPageAndSize
                                }>
                                <GridColumn
                                    type={GridColumnType.Date}
                                    field="date"
                                    header="Date"
                                    includeTime={true}
                                    width="50px"
                                />
                                <GridColumn
                                    field="message"
                                    header="Error Message"
                                />
                            </Grid>
                        ) : (
                            <div>No logs available</div>
                        )}
                    </DialogContent>
                    <DialogFooter>
                        <DialogCancel
                            text="Close"
                            onClick={this.closeLog.bind(this)}
                        />
                        <DialogOK
                            text="Reload"
                            onClick={this.reloadLog.bind(this)}
                        />
                        <DialogOK
                            text="Download"
                            tooltip="Download currently displayed log information"
                            onClick={this.downloadLog.bind(this)}
                        />
                    </DialogFooter>
                </Dialog>

                <ContextFragment name="integrations-menu">
                    <ContextMenuItem
                        id="integrations_spacer_0"
                        separator={true}
                        contextKey="integrations-file"
                        order={0}
                    />
                    <ContextMenuItem
                        id="integrations_delete"
                        icon="delete"
                        name="Delete"
                        contextKey="integrations-file"
                        handler={(e, c) =>
                            this.delete(
                                this.state.all.find(
                                    item =>
                                        `${item.type}-${item.id}` ===
                                        c.getValue(),
                                ),
                            )
                        }
                        order={4}
                    />
                    <ContextMenuItem
                        id="integrations_sync"
                        icon="sync"
                        name="Synchronise"
                        contextKey="integrations-file"
                        handler={(e, c) =>
                            this.sync(
                                this.state.all.find(
                                    item =>
                                        `${item.type}-${item.id}` ===
                                        c.getValue(),
                                ),
                            )
                        }
                        order={4}
                    />
                    <ContextMenuItem
                        id="integrations_spacer_1"
                        separator={true}
                        contextKey="integrations-menu"
                        order={0}
                    />
                    <ContextMenuItem
                        id="integrations_expand_all"
                        icon="add"
                        name="Expand All"
                        contextKey="integrations-menu"
                        handler={this.expandAll.bind(this)}
                        order={1}
                    />
                    <ContextMenuItem
                        id="integrations_collapse_all"
                        icon="remove"
                        name="Collapse All"
                        contextKey="integrations-menu"
                        handler={this.collapseAll.bind(this)}
                        order={2}
                    />
                </ContextFragment>
            </>
        );
    }
}

const mapStateToProps = (state: IAppState) => {
    const { integrations, campus } = state.db;
    const { session } = state.session;
    return {
        user: session.user,
        campuses: campus.campuses,
        lissData: integrations.lissData,
        lissResolutions: integrations.lissResolutions,
        sifData: integrations.sifData,

        logPageData: integrations.logPageData,
        pageSpec: integrations.virtualSpec,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        setPageLoading: (loading: boolean) => dispatch(setPageLoading(loading)),
        initialiseCampusDetails: () =>
            unwrappedDispatch(initialiseCampusDetails()),
        initialiseLISSIntegrations: () =>
            unwrappedDispatch(initialiseLISSIntegrations()),
        initialiseSIFIntegrations: () =>
            unwrappedDispatch(initialiseSIFIntegrations()),
        syncLISSIntegration: (integration: LISSIntegration) =>
            unwrappedDispatch(syncLISSIntegration(integration)),
        validateLISSIntegration: (integration: LISSIntegration) =>
            unwrappedDispatch(validateLISSIntegration(integration)),
        syncSIFIntegration: (integration: SIFIntegration) =>
            unwrappedDispatch(syncSIFIntegration(integration)),
        resetLISSCallState: () => dispatch(resetLISSCallState()),
        updateSIFIntegration: (integration: SIFIntegration) =>
            unwrappedDispatch(updateSIFIntegration(integration)),
        updateLISSIntegration: (integration: LISSIntegration) =>
            unwrappedDispatch(updateLISSIntegration(integration)),
        updateLISSIntegrationResolutions: (
            integration: LISSIntegration,
            resolutions: LISSResolutionRule[],
        ) =>
            unwrappedDispatch(
                updateLISSIntegrationResolutions({ integration, resolutions }),
            ),
        deleteLISSIntegration: (integration: LISSIntegration) =>
            unwrappedDispatch(deleteLISSIntegration(integration)),
        deleteSIFIntegration: (integration: SIFIntegration) =>
            unwrappedDispatch(deleteSIFIntegration(integration)),
        addExports: (exports: QueuedExportFile[]) =>
            unwrappedDispatch(addExports(exports)),
        openRecycled: (types: RecycledType[]) => openRecycled(types),
        initialiseLog: (type: string, id: number) =>
            unwrappedDispatch(initialiseLog({ type, id })),
        reinitialiseLog: (type: string, id: number) =>
            unwrappedDispatch(reinitialiseLog({ type, id })),
        setIntegrationLogPageAndSize: (specification: PageSpecification) =>
            unwrappedDispatch(setIntegrationLogPageAndSize(specification)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(Integrations);

interface LISSCallConfig {
    id: number;
    callName: string;
    name: string;
    enabled: boolean;
    canCreate: boolean;
    createId?: number;
    create: boolean;
}

interface LISSIntegrationFormProps {
    lissCalls: number[];
    lissCallData: LISSCallType[];
    lissStructures: LISSTimetableStructure[];

    binding: LISSIntegration;
    campus: Campus;
    campuses: Campus[];

    onChange: (newValue: LISSIntegration) => void;
    onLoadingChanged: (loading: boolean) => void;
    fetchLISSAdditionalConfig: (consumptionId: number) => Promise<void>;
    setLISSCallState: (callID: number, enabled: boolean) => void;
}

interface LISSIntegrationFormState {
    editingSync?: Date;
    showStructures?: boolean;

    callConfigs?: LISSCallConfig[];
}

class LISSIntegrationForm extends React.Component<
    LISSIntegrationFormProps,
    LISSIntegrationFormState
> {
    state: LISSIntegrationFormState = {};

    static getDerivedStateFromProps(
        nextProps: LISSIntegrationFormProps,
        prevState: LISSIntegrationFormState,
    ) {
        const timeRegMatch = /(\d\d):(\d\d):\d\d/.exec(
            nextProps.binding.syncAtTime,
        );
        const userCalls = nextProps.lissCallData.filter(
            call =>
                call.callName.startsWith("create") &&
                call.callName.endsWith("User"),
        );
        const nonUserCalls = nextProps.lissCallData
            .filter(call => !userCalls.includes(call))
            .map(
                call =>
                    ({
                        ...call,
                        enabled: nextProps.lissCalls.includes(call.id),
                        create: false,
                        canCreate: false,
                    } as LISSCallConfig),
            );
        for (const call of userCalls) {
            const callName = call.callName
                .replace("create", "get")
                .replace("User", "");
            const nonUserCall = nonUserCalls.find(
                c => c.callName === `${callName}s`,
            );

            if (nonUserCall) {
                nonUserCall.canCreate = true;
                nonUserCall.createId = call.id;
                nonUserCall.create = nextProps.lissCalls.includes(call.id);
            }
        }

        return {
            editingSync:
                timeRegMatch !== null
                    ? new Date(
                          0,
                          0,
                          0,
                          Number(timeRegMatch[1]),
                          Number(timeRegMatch[2]),
                      )
                    : new Date(0, 0, 0, 0, 0),
            showStructures: nextProps.lissStructures.length > 1,
            callConfigs: nonUserCalls,
        };
    }

    componentDidMount() {
        this.loadAdditionals();
        this.parseStructures();
    }

    componentDidUpdate(prevProps: LISSIntegrationFormProps) {
        if (prevProps.binding.id !== this.props.binding.id) {
            this.loadAdditionals();
        }

        this.parseStructures();
    }

    loadAdditionals() {
        if (!this.props.binding) return;

        try {
            this.props.onLoadingChanged(true);

            this.props.fetchLISSAdditionalConfig(this.props.binding.id);
        } finally {
            this.props.onLoadingChanged(false);
        }
    }

    parseStructures() {
        if (
            !this.props.binding?.ttStructure &&
            this.props.lissStructures?.length > 1
        ) {
            const today = new Date().toDate().dateOnly();
            const ttStructures = this.props.lissStructures.filter(
                f =>
                    f.startDate.compareDate(today) < 0 &&
                    f.endDate.compareDate(today) >= 0,
            );
            if (ttStructures.length === 1)
                this.props.onChange(
                    new LISSIntegration({
                        ...this.props.binding,
                        ttStructure: ttStructures[0].code,
                    }),
                );
        }
    }

    render() {
        return (
            <>
                <InputForm<LISSIntegration>
                    type={LISSIntegration}
                    binding={this.props.binding}
                    display={RenderOrientations.Vertical}
                    onChange={value => this.props.onChange(value)}>
                    <InputFormField
                        label="LISS Provider URL"
                        field="providerUrl"
                        mandatory={true}
                    />
                    <InputFormField
                        label="School"
                        field="schoolName"
                    />
                </InputForm>
                <InputForm<LISSIntegration>
                    type={LISSIntegration}
                    binding={this.props.binding}
                    display={RenderOrientations.Horizontal}
                    columns={2}
                    onChange={value => this.props.onChange(value)}>
                    <InputFormField
                        label="Campus"
                        field="campusID"
                        mandatory={true}
                        type={InputFieldTypes.Select}
                        options={this.props.campuses?.map(c => ({
                            value: c.id,
                            text: c.name,
                        }))}
                    />
                    <InputFormField
                        label="Academic Year"
                        field="termDatesID"
                        mandatory={true}
                        type={InputFieldTypes.Select}
                        options={
                            this.props.campus?.terms.map(c => ({
                                value: c.id,
                                text: c.display,
                            })) || []
                        }
                    />
                    <InputFormField
                        label="Username"
                        field="userName"
                    />
                    <InputFormField
                        type={InputFieldTypes.Password}
                        label="Password"
                        field="password"
                    />
                    <InputFormField
                        label="LISS Schema"
                        field="schema"
                        type={InputFieldTypes.Select}
                        options={[
                            {
                                text: "LISS Standard",
                                value: "",
                            },
                            {
                                text: "Compass Education",
                                value: "Compass",
                            },
                            {
                                text: "Posimente",
                                value: "Posimente",
                            },
                        ]}
                    />
                    <InputFormField
                        label="Timetable Structure"
                        field="ttStructure"
                        type={InputFieldTypes.Select}
                        hidden={!this.state.showStructures}
                        options={this.props.lissStructures.map(s => ({
                            value: s.code,
                            text: `${
                                s.code
                            } (${s.startDate?.toLocale?.()} - ${s.endDate?.toLocale?.()})`,
                        }))}
                    />
                    {this.props.binding.schema !== "Compass" && (
                        <InputFormField
                            type={InputFieldTypes.Number}
                            label="Past Days to Sync"
                            field="dO_PastHorizon"
                            minNumber={0}
                            maxNumber={14}
                        />
                    )}
                    {this.props.binding.schema !== "Compass" && (
                        <InputFormField
                            type={InputFieldTypes.Number}
                            label="Future Days to Sync"
                            field="dO_Horizon"
                            minNumber={0}
                            maxNumber={14}
                        />
                    )}
                </InputForm>
                {this.props.binding.schema === "Compass" && (
                    <GeneralContainer
                        fullWidth={true}
                        clearanceSide={Side.Top}
                        flex={FlexDirection.Column}
                        flexSpaced={true}>
                        <GeneralContainer
                            fullWidth={true}
                            flex={FlexDirection.Row}
                            flexSpaced={true}>
                            <Panel
                                className="half"
                                title="Class Dates">
                                <InputForm<LISSIntegration>
                                    type={LISSIntegration}
                                    binding={this.props.binding}
                                    display={RenderOrientations.Vertical}
                                    fieldDisplay={RenderOrientations.Horizontal}
                                    onChange={value =>
                                        this.props.onChange(value)
                                    }>
                                    <InputFormField
                                        type={InputFieldTypes.Date}
                                        label="Start Date"
                                        labelBold={true}
                                        field="tD_StartDate"
                                        mandatory={true}
                                    />
                                    <InputFormField
                                        type={InputFieldTypes.Date}
                                        label="End Date"
                                        labelBold={true}
                                        field="tD_EndDate"
                                        mandatory={true}
                                    />
                                </InputForm>
                            </Panel>
                            <Panel
                                className="half"
                                title="DO Range">
                                <InputForm<LISSIntegration>
                                    type={LISSIntegration}
                                    binding={this.props.binding}
                                    display={RenderOrientations.Vertical}
                                    fieldDisplay={RenderOrientations.Horizontal}
                                    onChange={value =>
                                        this.props.onChange(value)
                                    }>
                                    <InputFormField
                                        type={InputFieldTypes.Number}
                                        label="Past Days"
                                        labelBold={true}
                                        field="dO_PastHorizon"
                                        minNumber={0}
                                        maxNumber={14}
                                    />
                                    <InputFormField
                                        type={InputFieldTypes.Number}
                                        label="Future Days"
                                        labelBold={true}
                                        field="dO_Horizon"
                                        minNumber={0}
                                        maxNumber={14}
                                    />
                                </InputForm>
                            </Panel>
                        </GeneralContainer>
                        <Panel
                            className="full"
                            title="Timetable Range">
                            <InputForm<LISSIntegration>
                                type={LISSIntegration}
                                binding={this.props.binding}
                                display={RenderOrientations.Vertical}
                                fieldDisplay={RenderOrientations.Horizontal}
                                onChange={value => this.props.onChange(value)}>
                                <InputFormField
                                    type={InputFieldTypes.Number}
                                    label="Future Days"
                                    labelBold={true}
                                    field="tT_Horizon"
                                    minNumber={0}
                                    maxNumber={14}
                                />
                            </InputForm>
                        </Panel>
                    </GeneralContainer>
                )}
                <InputForm<LISSIntegration>
                    type={LISSIntegration}
                    clearanceSide={Side.Top}
                    display={RenderOrientations.Vertical}
                    fieldDisplay={RenderOrientations.Horizontal}>
                    <InputFormField
                        type={InputFieldTypes.Check}
                        label="Sync specific date"
                        reverse={true}
                        value={!!this.props.binding?.targetSyncDate}
                        onChange={(_, value) =>
                            this.props.onChange(
                                new LISSIntegration({
                                    ...this.props.binding,
                                    targetSyncDate: value
                                        ? new Date()
                                        : undefined,
                                }),
                            )
                        }
                    />
                    {!!this.props.binding?.targetSyncDate && (
                        <InputFormField
                            type={InputFieldTypes.Date}
                            value={this.props.binding?.targetSyncDate}
                            onChange={(_, value) =>
                                this.props.onChange(
                                    new LISSIntegration({
                                        ...this.props.binding,
                                        targetSyncDate: value,
                                    }),
                                )
                            }
                        />
                    )}
                </InputForm>
                <InputForm<LISSIntegration>
                    type={LISSIntegration}
                    clearanceSide={Side.Top}
                    display={RenderOrientations.Vertical}
                    fieldDisplay={RenderOrientations.Vertical}>
                    <InputFormField
                        display={RenderOrientations.Vertical}
                        type={InputFieldTypes.Radio}
                        value={this.props.binding?.active}
                        onChange={(_, value) =>
                            this.props.onChange(
                                new LISSIntegration({
                                    ...this.props.binding,
                                    active: value,
                                }),
                            )
                        }
                        options={[
                            {
                                text: "Manual sync only",
                                value: false,
                            },
                            {
                                text: "Sync at specific time",
                                value: true,
                            },
                        ]}
                    />
                    {this.props.binding?.active && (
                        <InputFormField
                            type={InputFieldTypes.Time}
                            value={this.state.editingSync}
                            onChange={(_, value) =>
                                this.setState({
                                    editingSync: value,
                                })
                            }
                        />
                    )}
                </InputForm>

                <Panel clearanceSide={Side.Top}>
                    <Info>
                        Select the integration calls you want to perform with
                        the LISS partner.
                    </Info>

                    <Grid<LISSCallConfig>
                        value={this.state.callConfigs}
                        fullWidth={true}
                        keyField="id"
                        valueEdited={(record, value) => {
                            if (Utility.notNull(value.enabled))
                                this.props.setLISSCallState(
                                    record.id,
                                    value.enabled,
                                );
                            if (Utility.notNull(value.create))
                                this.props.setLISSCallState(
                                    record.createId,
                                    value.create,
                                );
                        }}>
                        <GridColumn
                            type={GridColumnType.Checkbox}
                            field="enabled"
                            header=""
                            editable={true}
                        />
                        <GridColumn
                            field="name"
                            header="Name"
                        />
                        <GridColumn
                            type={GridColumnType.Checkbox}
                            field="create"
                            header="Create Users"
                            editable={true}
                            visibleField="canCreate"
                            width="50px"
                        />
                    </Grid>
                </Panel>
            </>
        );
    }
}

const mapCompassStateToProps = (state: IAppState) => {
    const { integrations } = state.db;
    return {
        lissCalls: integrations.lissCalls,
        lissCallData: integrations.lissCallData,
        lissStructures: integrations.lissStructures,
    };
};

const mapCompassDispatchToProps = dispatch => {
    return {
        fetchLISSAdditionalConfig: (consumptionId: number) =>
            unwrappedDispatch(fetchLISSAdditionalConfig(consumptionId)),
        setLISSCallState: (callID: number, enabled: boolean) =>
            dispatch(setLISSCallState({ call: callID, enabled })),
    };
};

const ConnectedLISSIntegrationForm = connect(
    mapCompassStateToProps,
    mapCompassDispatchToProps,
)(LISSIntegrationForm);

interface SIFIntegrationFormProps {
    binding: SIFIntegration;
    campus: Campus;
    campuses: Campus[];

    onChange: (newValue: SIFIntegration) => void;
}

interface SIFIntegrationFormState {
    editingSync?: Date;
}

class SIFIntegrationForm extends React.Component<
    SIFIntegrationFormProps,
    SIFIntegrationFormState
> {
    state: SIFIntegrationFormState = {};

    static getDerivedStateFromProps(
        nextProps: SIFIntegrationFormProps,
        prevState: SIFIntegrationFormState,
    ) {
        const timeRegMatch = /(\d\d):(\d\d):\d\d/.exec(
            nextProps.binding.syncAtTime,
        );
        return {
            editingSync:
                timeRegMatch !== null
                    ? new Date(
                          0,
                          0,
                          0,
                          Number(timeRegMatch[1]),
                          Number(timeRegMatch[2]),
                      )
                    : new Date(0, 0, 0, 0, 0),
        };
    }

    render() {
        return (
            <>
                <InputForm<SIFIntegration>
                    type={SIFIntegration}
                    binding={this.props.binding}
                    display={RenderOrientations.Vertical}
                    onChange={value => this.props.onChange(value)}>
                    <InputFormField
                        label="SIF Provider URL"
                        field="providerURL"
                        mandatory={true}
                    />
                </InputForm>

                <InputForm<SIFIntegration>
                    type={SIFIntegration}
                    binding={this.props.binding}
                    display={RenderOrientations.Horizontal}
                    columns={2}
                    onChange={value => this.props.onChange(value)}>
                    <InputFormField
                        label="Name"
                        field="name"
                        mandatory={true}
                    />
                    <InputFormField
                        label="User Token"
                        field="userToken"
                    />
                    <InputFormField
                        label="Campus"
                        field="campusID"
                        mandatory={true}
                        type={InputFieldTypes.Select}
                        options={this.props.campuses?.map(c => ({
                            value: c.id,
                            text: c.name,
                        }))}
                    />
                    <InputFormField
                        label="Academic Year"
                        field="termDatesID"
                        mandatory={true}
                        type={InputFieldTypes.Select}
                        options={
                            this.props.campus?.terms.map(c => ({
                                value: c.id,
                                text: c.display,
                            })) || []
                        }
                    />
                    <InputFormField
                        label="Application Key"
                        field="applicationKey"
                        mandatory={true}
                    />
                    <InputFormField
                        type={InputFieldTypes.Password}
                        label="Shared Secret"
                        field="sharedSecret"
                        mandatory={true}
                    />
                    <InputFormField
                        label="Environment Token"
                        field="environmentToken"
                    />
                    <InputFormField
                        label="Session Token"
                        field="sessionToken"
                    />
                    <InputFormField
                        label="Authentication Mode"
                        field="authMode"
                        type={InputFieldTypes.Select}
                        options={Utility.enumToOptions(SIFAuthModes, "string")}
                    />
                </InputForm>
                <InputForm<SIFIntegration>
                    type={SIFIntegration}
                    clearanceSide={Side.Top}
                    display={RenderOrientations.Vertical}
                    fieldDisplay={RenderOrientations.Vertical}>
                    <InputFormField
                        display={RenderOrientations.Vertical}
                        type={InputFieldTypes.Radio}
                        value={this.props.binding?.active}
                        onChange={(_, value) =>
                            this.props.onChange(
                                new SIFIntegration({
                                    ...this.props.binding,
                                    active: value,
                                }),
                            )
                        }
                        options={[
                            {
                                text: "Manual sync only",
                                value: false,
                            },
                            {
                                text: "Sync at specific time",
                                value: true,
                            },
                        ]}
                    />
                    {this.props.binding?.active && (
                        <InputFormField
                            type={InputFieldTypes.Time}
                            value={this.state.editingSync}
                            onChange={(_, value) =>
                                this.setState({
                                    editingSync: value,
                                })
                            }
                        />
                    )}
                </InputForm>
            </>
        );
    }
}
